import { useState, useMemo } from 'react';

import { MessageMedium } from 'modules/graphql/types';
import { Button, BUTTON_SIZES } from 'ui/button';
import { CircleSpinner } from 'ui/spinner';
import { FormField } from 'ui/formField';
import { ModalFooter, ModalHeader, MODAL_VARIANTS, MODAL_SIZES } from 'ui/modal';
import { BasicFilterSelect } from 'ui/selects';

import { displayContent, sendProofFilter } from 'modules/templateEditor/common';
import {
  useSendTestMsgContext,
  TestMsgModals,
} from 'modules/templateEditor/context/SendTestMsgContext';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { StyledModal, StyledModalBody, StyledModalSubheader } from '../SendTestMsgModals.styled';

export const DISPLAY_TEST_MESSAGE_MODAL_TEST_ID = 'display-test-message-modal';

export const SendTestMsgToInternalListModal = () => {
  const {
    internalListOptions,
    isFetchingLists,
    onSendTestToInternalList,
    setOpenTestMsgModal,
    isSendingTestMsg,
  } = useSendTestMsgContext();
  const { medium } = useTemplateQueryContext();
  const [selectedListId, setSelectedListId] = useState<number>();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const memoizedSelectedOptions = useMemo(() => {
    return sendProofFilter(searchTerm, internalListOptions);
  }, [internalListOptions, searchTerm]);

  const handleCloseModal = () => {
    setOpenTestMsgModal(TestMsgModals.None);
  };

  const handleSendTestMsg = () => {
    if (selectedListId) {
      onSendTestToInternalList(selectedListId);
      handleCloseModal();
    }
  };

  return (
    <StyledModal
      id="sendTestMsgToInternalListModal"
      isModalOpen={true}
      onClose={handleCloseModal}
      data-test={DISPLAY_TEST_MESSAGE_MODAL_TEST_ID}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>
        {displayContent.strSendTestToInternalListTitle({ isEmail: medium === MessageMedium.Email })}
      </ModalHeader>
      {isFetchingLists ? (
        <CircleSpinner />
      ) : (
        <>
          <StyledModalSubheader>
            {displayContent.strSendTestToInternalListDesc({
              isEmail: medium === MessageMedium.Email,
              msgType: medium,
            })}
          </StyledModalSubheader>
          <StyledModalBody>
            <FormField labelText={displayContent.labelListName}>
              <BasicFilterSelect
                data-test="select-test-msg-recipient-list"
                defaultValues={[displayContent.placeholderSelectList]}
                id="select-test-msg-recipient-list"
                inputContent={{ filterPlaceholder: displayContent.placeholderSearchList }}
                isVirtualized
                onSelect={([newListId]) => setSelectedListId(parseInt(newListId, 10))}
                options={memoizedSelectedOptions}
                onSearchChange={(searchedItem) => setSearchTerm(searchedItem ?? '')}
                disableInternalFiltering
                isFullWidth
              />
            </FormField>
          </StyledModalBody>
        </>
      )}

      <ModalFooter>
        <Button
          isLoading={isSendingTestMsg}
          isDisabled={!selectedListId}
          onClick={handleSendTestMsg}
          size={BUTTON_SIZES.LARGE}
        >
          {displayContent.btnSend({ isEmail: medium === MessageMedium.Email })}
        </Button>
      </ModalFooter>
    </StyledModal>
  );
};
